// SocialLogin.jsx - Fixed
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress } from "@mui/material";
import { signInWithPopup, getIdToken, GoogleAuthProvider } from "firebase/auth";
import Cookies from "universal-cookie";
import { auth, googleProvider } from "../../firebase";
import { google } from "../../config/Pageitems";
import config from "../../config.js";
import { redirectToTenantHost } from "../../utils/tenantAuthHandoff";
const API_URL = `${config.baseURL}/auth/login?type=firebase`;

// Create a custom fetch function with better timeout handling
const fetchWithTimeout = async (url, options, timeout = 30000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    if (error.name === "AbortError") {
      throw new Error("Request timeout");
    }
    throw error;
  }
};

const SocialLogin = ({
  loading,
  setLoading,
  onSuccess,
  onError,
  dispatch,
  navigate,
}) => {
  const { t } = useTranslation();
  const [googleLoading, setGoogleLoading] = useState(false);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Determine if any login method is currently loading
  const isLoading = loading || googleLoading;

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    // Create a new provider instance each time to avoid caching issues
    const provider = new GoogleAuthProvider();

    // Configure Google Auth to always show account selection
    provider.setCustomParameters({
      prompt: "select_account", // Forces account selection every time
      access_type: "online", // Don't use offline access
      approval_prompt: "force", // Force the approval prompt to show
      ux_mode: "popup", // Use popup flow instead of redirect
    });
    try {
      // Step 1: Firebase Google Authentication
      const result = await signInWithPopup(auth, googleProvider);

      // Check if component is still mounted
      if (!mountedRef.current) return;

      const user = result.user;

      // Step 2: Get Firebase ID token
      const token = await getIdToken(user);

      // Check if component is still mounted
      if (!mountedRef.current) return;

      try {
        // Step 3: Send token to backend for verification with timeout
        const response = await fetchWithTimeout(
          API_URL,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ firebaseToken: token }),
            credentials: "include",
          },
          12000 // 12 second timeout
        );

        // Check if component is still mounted
        if (!mountedRef.current) return;

        if (response.ok) {
          const data = await response.json();

          // Check if component is still mounted
          if (!mountedRef.current) return;

          console.log("[GOOGLE_AUTH] Response data received:", data);

          // Set cookie
          const cookies = new Cookies();
          cookies.set("token", token, {
            path: "/",
            secure: true,
            sameSite: "strict",
            expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
          });

          const mappedFeatures = data.userRoleWithAccess?.features
            ? data.userRoleWithAccess.features.map((feature) => ({
                action: feature.feature?.action || "",
                api_path: feature.feature?.api_path || "",
                name: feature.feature?.name || "",
                description: feature.feature?.description || "",
                isDefault: feature.feature?.isDefault || false,
                displayName: feature.feature?.displayName || "",
              }))
            : [];
          const accessToken = data.accessToken || data.token || token;
          const orgTypes = Array.isArray(data.organization?.organizationType)
            ? data.organization.organizationType
            : data.organization?.organizationType
              ? [data.organization.organizationType]
              : [];
          const isStaffOnly =
            orgTypes.includes("staffTrack") &&
            !orgTypes.includes("vehicleTrack") &&
            !orgTypes.includes("schoolTrack");
          if (
            !isStaffOnly &&
            redirectToTenantHost({
              organization: data.organization,
              user: data.tokenuser
                ? {
                    ...data.tokenuser,
                    role: data.userRoleWithAccess?.name || "Unknown",
                  }
                : null,
              features: mappedFeatures,
              accessToken,
              refreshToken: data.refreshToken || "",
              rememberMe: true,
            })
          ) {
            return;
          }

          // Store user data in Redux - with safety checks
          if (dispatch && mountedRef.current) {
            try {
              console.log("[GOOGLE_AUTH] Response structure:", {
                hasTokenuser: !!data.tokenuser,
                hasUserRole: !!data.userRoleWithAccess,
              });

              // Use correct lowercase 'tokenuser' from response
              if (data.tokenuser) {
                console.log("[GOOGLE_AUTH] Dispatching user data");
                dispatch({
                  type: "SET_USER",
                  payload: {
                    ...data.tokenuser,
                    role: data.userRoleWithAccess?.name || "Unknown",
                  },
                });
                console.log("[GOOGLE_AUTH] User dispatched successfully");
              }

              // Check if features exist and are in correct format
              if (
                data.userRoleWithAccess?.features &&
                Array.isArray(data.userRoleWithAccess.features)
              ) {
                console.log(
                  "[GOOGLE_AUTH] Dispatching features, count:",
                  data.userRoleWithAccess.features.length
                );
                const mappedFeatures = data.userRoleWithAccess.features.map(
                  (feature) => ({
                    action: feature.feature?.action || "",
                    api_path: feature.feature?.api_path || "",
                    name: feature.feature?.name || "",
                    description: feature.feature?.description || "",
                    isDefault: feature.feature?.isDefault || false,
                    displayName: feature.feature?.displayName || "",
                  })
                );
                console.log("[GOOGLE_AUTH] Mapped features:", mappedFeatures);

                dispatch({
                  type: "SET_FEATURES",
                  payload: mappedFeatures,
                });
                console.log("[GOOGLE_AUTH] Features dispatched successfully");
              } else {
                console.warn("[GOOGLE_AUTH] No features found in response");
                dispatch({
                  type: "SET_FEATURES",
                  payload: [],
                });
              }

              // Dispatch organization if available
              if (data.organization) {
                console.log("[GOOGLE_AUTH] Dispatching organization data");
                dispatch({
                  type: "SET_ORGANIZATION",
                  payload: data.organization,
                });
                console.log(
                  "[GOOGLE_AUTH] Organization dispatched successfully"
                );
              }
            } catch (dispatchError) {
              //console.error("Error in dispatch:", dispatchError);
              // Continue with login success even if dispatch fails
            }
          }

          // Wait for state to persist before navigating (same as StandardLoginForm)
          if (typeof onSuccess === "function" && mountedRef.current) {
            setTimeout(() => {
              if (mountedRef.current) {
                onSuccess("Google login successful!", {
                  ...data.tokenuser,
                  organization: data.organization, 
                });
              }
            }, 500); // Give context time to save to localStorage
          }

          // Let the parent component handle navigation via onSuccess callback
          // This prevents double navigation attempts
        } else {
          const errorData = await response
            .json()
            .catch(() => ({ message: "Unknown error" }));
          throw new Error(errorData.message || "Authentication failed");
        }
      } catch (backendError) {
        //console.error("Backend error:", backendError);

        // Check if component is still mounted
        if (!mountedRef.current) return;

        let errorMessage = "No account found with this email. Please verify the email or sign up.";
        if (backendError.message === "Request timeout") {
          errorMessage = "Server response timeout. Please try again.";
        }

        // Do not set cookies or redirect on backend error
        if (typeof onError === "function") {
          onError(errorMessage);
        }
      }
    } catch (error) {
      //console.error("Google login error:", error);

      // Check if component is still mounted
      if (!mountedRef.current) return;

      // Handle specific Google Auth errors
      let errorMessage;
      if (error.code === "auth/popup-closed-by-user") {
        errorMessage = "Google login canceled. Please try again.";
      } else if (error.code === "auth/popup-blocked") {
        errorMessage =
          "Pop-up was blocked by your browser. Please enable pop-ups for this site.";
      } else if (
        error.code === "auth/account-exists-with-different-credential"
      ) {
        errorMessage =
          "An account already exists with the same email address but different sign-in credentials.";
      } else if (error.code === "auth/network-request-failed") {
        errorMessage =
          "Network error. Please check your connection and try again.";
      } else if (error.code === "auth/user-disabled") {
        errorMessage =
          "This account has been disabled. Please contact support.";
      } else if (error.code === "auth/cancelled-popup-request") {
        // This is a common case when user clicks multiple times, we can ignore this error
        //console.log("Popup request cancelled");
        if (mountedRef.current) {
          setGoogleLoading(false);
        }
        return; // Don't show error message for this case
      } else {
        errorMessage = error.message || "Failed to sign in with Google";
      }

      if (typeof onError === "function" && mountedRef.current) {
        onError(errorMessage);
      }
    } finally {
      if (mountedRef.current) {
        setGoogleLoading(false);
      }
    }
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", margin: "20px 0" }}>
        <div style={{ flex: 1, height: "1px", backgroundColor: "#E0E0E0" }} />
        <div style={{ margin: "0 10px", color: "#757575", fontWeight: "500" }}>
          {t("OR")}
        </div>
        <div style={{ flex: 1, height: "1px", backgroundColor: "#E0E0E0" }} />
      </div>
      <Button
        onClick={handleGoogleLogin}
        disabled={isLoading}
        variant="outlined"
        fullWidth
        startIcon={
          !googleLoading && (
            <img src={google} alt="Google" style={{ width: 24, height: 24 }} />
          )
        }
        style={{
          borderRadius: "16px",
          border: "1px solid #95A0B3",
          padding: "12px",
          textTransform: "none",
          fontSize: "16px",
          fontWeight: "500",
          color: "#414958",
          background: "#E8EAEE",
          position: "relative",
          height: "48px",
        }}
      >
        {googleLoading ? (
          <CircularProgress
            size={20}
            sx={{ color: "#414958", position: "absolute" }}
          />
        ) : (
          t("Continue with Google")
        )}
      </Button>
    </>
  );
};

export default SocialLogin;
